import { z } from "zod";
import type { Tool } from "@modelcontextprotocol/sdk/types.js";
import type { AuthProvider } from "../auth/types.js";
import {
  CopilotEvaluationConnectionsApi,
  CopilotEvaluationConnectionsError,
  EVALUATION_BOT_SCHEMA_PATTERN,
  EVALUATION_ENVIRONMENT_PATTERN,
} from "../api/copilot-evaluation-connections-api.js";
import { GUID_PATTERN } from "../utils/security.js";

export const getCopilotEvaluationConnectionsInputSchema = z
  .object({
    environment: z.string().regex(EVALUATION_ENVIRONMENT_PATTERN).optional(),
    agentId: z.string().regex(GUID_PATTERN),
    agentSchemaName: z.string().regex(EVALUATION_BOT_SCHEMA_PATTERN),
    includeErrorDiagnostics: z.boolean().optional(),
  })
  .strict();

export const getCopilotEvaluationConnectionsTool: Tool = {
  name: "get_copilot_agent_evaluation_connections",
  description:
    "EXPERIMENTAL read-only evaluation-channel connection metadata for the current caller: bounded binding/connector/connection IDs, status labels and operation IDs (flow and AI-model bindings are counts only). Fixed pva-maker-evaluation channel, existing silent authentication, one bounded GET, no retries or redirects. Optional includeErrorDiagnostics reads a bounded JSON error body and returns only allowlisted scope labels from a closed list \u2014 NOT proven required permissions; default false never reads the error body. This is current evaluation-channel metadata, NOT a historical run mapping, a granted-scope check, or proof of runtime access or agent execution success.",
  annotations: { readOnlyHint: true, destructiveHint: false },
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      environment: {
        type: "string",
        pattern: EVALUATION_ENVIRONMENT_PATTERN.source,
        description:
          "Environment GUID or Default-GUID. Defaults to the current session environment; names and URLs are not accepted.",
      },
      agentId: { type: "string", pattern: GUID_PATTERN.source },
      agentSchemaName: { type: "string", pattern: EVALUATION_BOT_SCHEMA_PATTERN.source },
      includeErrorDiagnostics: {
        type: "boolean",
        default: false,
        description:
          "Opt in to at most 4 KiB of same-request JSON error-body inspection. Only known code labels and closed-allowlist scope-name mentions are returned; unknown names are omitted. Mentions are not proof a permission is required.",
      },
    },
    required: ["agentId", "agentSchemaName"],
  },
};

export async function handleGetCopilotEvaluationConnections(
  context: { authProvider?: AuthProvider; defaultEnvironmentId: string },
  input: unknown,
) {
  const parsed = getCopilotEvaluationConnectionsInputSchema.parse(input);
  if (!context.authProvider)
    throw new CopilotEvaluationConnectionsError(
      "Read Copilot Studio evaluation connections: existing authentication provider is unavailable; no sign-in was attempted.",
      401,
    );
  const metadata = await new CopilotEvaluationConnectionsApi(context.authProvider).getConnections(
    {
      environmentId: parsed.environment ?? context.defaultEnvironmentId,
      agentId: parsed.agentId,
      agentSchemaName: parsed.agentSchemaName,
    },
    { includeErrorDiagnostics: parsed.includeErrorDiagnostics },
  );
  return {
    text: "Read experimental current-caller evaluation-channel connection metadata. Missing or filtered fields remain unavailable. This does not identify a historical run's connections or verify consent, granted scopes, runtime access, or agent execution success. No agent or connector operation was executed and no binding or authentication was changed.",
    structured: { evaluationConnections: metadata },
  };
}
